import React, { useEffect, useMemo, useRef, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';

import { LayerContext } from 'state/LayerContext';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import {
  getWorkflowState,
  selectCurrentScenarioId,
  selectMaximize,
  selectProject,
  selectTypicalMonth,
  setScenarioSetupUIMode,
  setTypicalMonth,
} from 'state/workflowSlice';

import { PROCESS_STATUS, ScenarioSetupUIMode } from 'appConstants';
import StlModal from 'features/StlModal/StlModal';
import { StlButton } from 'features/common/StlButton/StlButton';
import StlViewAllModal from 'features/common/StlViewAllModal/StlViewAllModal';
import { capitalizeFirstLetter } from 'features/common/utils';
import { MapContextProvider } from 'features/map/MapContext';
import { withSegmentTooltip } from 'features/map/hooks/withSegmentTooltip';
import LoadingSpinner from 'features/spinner/Spinner';
import { workflowStyles } from 'features/workspace/workspaceUtils';

import {
  selectScenarioStatus,
  selectViewAllModal,
  selectVisualize,
  setViewAllModal,
  setVisualize,
  useGetScenariosQuery,
  useUpdateViewDateScenarioMutation,
} from '../../state/detourSlices';
import { selectScenarioSetupUIMode } from '../../state/workflowSlice';
import NoAccessPopup from '../common/NoAccessPopup';
import { ReactComponent as AddElements } from '../common/icons/add_elements.svg';
import { ReliesOn } from '../workflow_common/NoDataLoadedWarning';
import { WorkflowInfo } from '../workflow_common/workflowLookup';
import { DetourMap } from './DetourMap';
import DetourSidebar from './DetourSidebar';
import { DetourTimeSlider } from './DetourTimeSlider';
import { useScenarioSettingsData } from './ScenarioDataProvider';
import { ScenarioList } from './ScenarioList';
import { useGetScenarioPeriodsQuery } from './ScenarioMonthPicker';
import { CollapseScenarioTravelTimeChart } from './ScenarioTravelTimeChart';
import { useScenarioUI } from './hooks/useScenarioUI';

import styles from '../workspace/Workspace.module.css';

export const workflowDetour: WorkflowInfo = {
  dataAvailableReliesOn: [ReliesOn.detourMonth],
  switchToState: (fromWorkflow, state) => {
    // initial state
    getWorkflowState(state).scenarioMode = ScenarioSetupUIMode.Scenarios;
  },
};

const MODAL_DESCR = `Run a scenario for a road segment closure.
See traffic impact, rerouting options, and potential delays instantly.`;
const MODAL_TITLE = `Create New Scenario To Get Started`;

const DetourMapWithTooltip = withSegmentTooltip(DetourMap);

const itemName = 'scenario';

export function WorkflowDetour() {
  const dispatch = useAppDispatch();

  const [layer, setLayer] = useState(null);

  const currentScenarioId = useAppSelector(selectCurrentScenarioId);
  const maximize = useAppSelector(selectMaximize);
  const project = useAppSelector(selectProject);
  const uiMode = useAppSelector(selectScenarioSetupUIMode);
  const typicalMonth = useAppSelector(selectTypicalMonth);
  const visualize = useAppSelector(selectVisualize);
  const viewAllModal = useAppSelector(selectViewAllModal);
  const scenarioStatus = useAppSelector(selectScenarioStatus);
  const [updateViewDate] = useUpdateViewDateScenarioMutation();
  const { map_style } = workflowStyles(maximize);
  const { data: detourMonthsInfo } = useGetScenarioPeriodsQuery(project, {
    skip: !project,
  });

  const { isLoading: scenarioLoading, notFoundError } =
    useScenarioSettingsData();

  const {
    currentData: pastScenarios,
    isLoading,
    isFetching,
  } = useGetScenariosQuery(project, { skip: !project });
  const loading = isLoading || isFetching || !project;

  // memoize so this doesn't change every render.
  const memoizedLayer = useMemo(() => ({ layer, setLayer }), [layer]);
  const { setCreateMode, setInitialMode } = useScenarioUI();

  const prevScenarioId = useRef<string | null>(null);
  useEffect(() => {
    if (currentScenarioId && currentScenarioId !== prevScenarioId.current)
      updateViewDate({ project_slug: project, scenario_id: currentScenarioId });
    prevScenarioId.current = currentScenarioId;
  }, [currentScenarioId]);

  // ensure correct uiMode + correct init state
  useEffect(() => {
    dispatch(setScenarioSetupUIMode(ScenarioSetupUIMode.Scenarios));
    dispatch(setVisualize(true));
  }, [project]);

  useEffect(() => {
    let new_layer = null;
    if (detourMonthsInfo && detourMonthsInfo.length > 0) {
      if (typicalMonth) {
        new_layer = detourMonthsInfo.find(
          (info) => info.typicalMonth === typicalMonth,
        )?.layer;
      } else {
        dispatch(setTypicalMonth(detourMonthsInfo[0].typicalMonth));
      }
    }
    // console.log(`WorkflowPlan set new layer ${new_layer} for typicalMonth ${typicalMonth}`);
    setLayer(new_layer);
  }, [detourMonthsInfo, typicalMonth, setLayer]);

  let mainContent: React.ReactNode;
  let loadingContent: React.ReactNode;
  if (loading || scenarioLoading) {
    loadingContent = (
      <div className={styles.loader_wrapper}>
        <LoadingSpinner large />
      </div>
    );
  } else if (notFoundError)
    loadingContent = (
      <NoAccessPopup
        open={notFoundError}
        itemName={itemName}
        onClose={setInitialMode}
      />
    );

  if (
    !loading &&
    pastScenarios?.scenarios?.length === 0 &&
    uiMode !== ScenarioSetupUIMode.Create
  ) {
    mainContent = (
      <StlModal
        title={MODAL_TITLE}
        descr={MODAL_DESCR}
        upper={<AddElements width={209} height={140} />}
        lower={
          <div>
            <StlButton
              width={176}
              handleClick={setCreateMode}
              isActive
              bold
              icon={<AddIcon sx={{ fontSize: '24px !important' }} />}
              value="Create New Scenario"
            />
          </div>
        }
      />
    );
  } else if (
    viewAllModal ||
    (uiMode !== ScenarioSetupUIMode.Create && currentScenarioId === undefined)
  ) {
    mainContent = (
      <StlViewAllModal
        itemName={capitalizeFirstLetter(itemName)}
        title={'Closure Scenarios'}
        handleCreate={setCreateMode}
        listComponent={ScenarioList}
        showCloseBtn={viewAllModal}
        onClose={() => dispatch(setViewAllModal(false))}
        nameAccessor="scenario_name"
      />
    );
  }

  const workflowReady = !(loading || notFoundError);

  const showTimeSlider =
    workflowReady &&
    uiMode === ScenarioSetupUIMode.Scenarios &&
    currentScenarioId &&
    scenarioStatus === PROCESS_STATUS.DONE &&
    visualize;

  return (
    <LayerContext.Provider value={memoizedLayer}>
      <MapContextProvider>
        <div
          className={styles.workspace_container}
          id={styles.workspace_container_detour}
        >
          <div
            className={styles.workspace_content}
            style={{ backgroundColor: 'var(--chart_background_color)' }}
          >
            <div className={map_style}>
              <DetourMapWithTooltip
                showTooltip={
                  uiMode === ScenarioSetupUIMode.Create ||
                  uiMode === ScenarioSetupUIMode.Edit
                }
                enableHover={
                  uiMode === ScenarioSetupUIMode.Edit ||
                  uiMode === ScenarioSetupUIMode.Create ||
                  (uiMode === ScenarioSetupUIMode.Scenarios &&
                    scenarioStatus === PROCESS_STATUS.DONE &&
                    visualize)
                }
              />
            </div>
            {showTimeSlider && <DetourTimeSlider />}
            <CollapseScenarioTravelTimeChart />
          </div>
          {(currentScenarioId || uiMode === ScenarioSetupUIMode.Create) && (
            <DetourSidebar />
          )}
          {loadingContent}
          {mainContent}
        </div>
      </MapContextProvider>
    </LayerContext.Provider>
  );
}
